import { useState, useEffect } from "react";
import { Box, Button, Grid, Paper, Stack, TextField, Typography } from "@mui/material";
import { RowData } from "../types";

type MismatchedAddressEditorProps = {
  mismatchedTransactions: RowData[];
  onCancel: () => void;
  onUpdate: (updatedTransactions: RowData[]) => void;
};

function MismatchedAddressEditor({ 
  mismatchedTransactions, 
  onCancel, 
  onUpdate 
}: MismatchedAddressEditorProps) {
  // Initialize updatedAddresses with default values from transactions
  const [updatedAddresses, setUpdatedAddresses] = useState<Record<string, string>>({});
  
  // Initialize the default addresses when component mounts or transactions change
  useEffect(() => {
    const initialAddresses: Record<string, string> = {};
    
    mismatchedTransactions.forEach((tx: any) => {
      const invoice_no = tx.invoice_no || "";
      const address = tx.bankAccountValue?.bank_details?.account_holder_address || "";
      const residingCountry = tx.bankAccountValue?.bank_details?.account_holder_residing_country || "";
      
      // Add comma before residing country if both address and residing country exist
      const formattedAddress = address && residingCountry 
        ? `${address}, ${residingCountry}` 
        : `${address}${residingCountry}`;
      
      initialAddresses[invoice_no] = formattedAddress;
    });
    
    setUpdatedAddresses(initialAddresses);
  }, [mismatchedTransactions]);

  const handleAddressChange = (invoice_no: string, newAddress: string) => {
    setUpdatedAddresses(prev => ({
      ...prev,
      [invoice_no]: newAddress
    }));
  };

  const handleUpdateAndContinue = () => {
    // Create updated transactions with the new addresses
    const updatedTransactions = mismatchedTransactions.map(tx => {
      const invoice_no = tx.invoice_no || "";
      
      // Always use the current state value, even if it's empty
      if (invoice_no in updatedAddresses) {
        // Deep clone the transaction to avoid mutation
        const updatedTx = JSON.parse(JSON.stringify(tx));
        
        // Update the address details
        if (updatedTx.bankAccountValue?.bank_details) {
          const currentAddress = updatedAddresses[invoice_no];
          
          // Store the entire address in account_holder_address and clear residing country
          updatedTx.bankAccountValue.bank_details.account_holder_address = currentAddress;
          updatedTx.bankAccountValue.bank_details.account_holder_residing_country = "";
        }
        
        return updatedTx;
      }
      return tx;
    });

    onUpdate(updatedTransactions);
  };

  return (
    <Box sx={{ py: 2 }}>
      <Typography color="error" variant="subtitle1" sx={{ mb: 3 }}>
        The following transactions have addresses that don't match their countries.
        Please review and update them before proceeding.
      </Typography>
      
      <Stack spacing={3} sx={{ mb: 3 }}>
        {mismatchedTransactions.map((tx: any) => {
          const invoice_no = tx.invoice_no || "";
          const country = tx.bankAccountValue?.country || "";
          
          return (
            <Paper key={invoice_no} variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                {tx.expert_name || ""}
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Country (Read-only)"
                    value={country}
                    fullWidth
                    disabled
                    size="small"
                    variant="outlined"
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Address"
                    value={updatedAddresses[invoice_no] ?? ""}
                    onChange={(e) => handleAddressChange(invoice_no, e.target.value)}
                    fullWidth
                    size="small"
                    variant="outlined"
                  />
                </Grid>
              </Grid>
            </Paper>
          );
        })}
      </Stack>
      
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button 
          variant="outlined" 
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button 
          variant="contained" 
          color="primary"
          onClick={handleUpdateAndContinue}
        >
          Update and Continue
        </Button>
      </Box>
    </Box>
  );
}

export default MismatchedAddressEditor;